// Min resa: Björn's own numbers, in his own words. Weight, GKI (only when he
// has actually measured — no program vocabulary otherwise), mood, and the
// latest weekly letter.
//
// The charts are hand-rolled inline SVG: no library, no axes, no grid. Every
// figure is repeated as a plain Swedish sentence, because the sentence is what
// he reads. Colours come from the CSS custom properties, so both themes work.
// Honesty rules (invariant 8) apply here too: an empty series says it is
// empty, and nothing is smoothed into a story the data does not carry.

import { getLatestBrief } from "../api";
import { SERVER_URL, getToken } from "../config";
import { clear, el } from "../dom";
import { getLang, t } from "../strings";

import { getStats, type Stats } from "../api";

const SVG_NS = "http://www.w3.org/2000/svg";
const W = 320;
const H = 104;
const PAD_X = 8;
const PAD_Y = 12;
const MAX_GAP_DAYS = 3; // a longer hole is a hole: the line breaks instead of guessing
const WINDOW_DAYS = 60; // weight and GKI both come from the server as 60-day series
const MOOD_DAYS = 30;

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
  return node;
}

function chartRoot(label: string): SVGSVGElement {
  const svg = svgEl("svg", {
    class: "spark",
    viewBox: `0 0 ${W} ${H}`,
    role: "img",
    "aria-label": label,
  });
  return svg;
}

function locale(): string {
  return getLang() === "en" ? "en-GB" : "sv-SE";
}

function dateMs(date: string): number {
  return Date.parse(`${date}T12:00:00Z`);
}

function daysBetween(from: string, to: string): number {
  return Math.round((dateMs(to) - dateMs(from)) / 86_400_000);
}

function addDays(date: string, days: number): string {
  return new Date(dateMs(date) + days * 86_400_000).toISOString().slice(0, 10);
}

function fmtDate(date: string): string {
  return new Intl.DateTimeFormat(locale(), {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(dateMs(date)));
}

function fmtNumber(value: number, decimals: number): string {
  return value.toLocaleString(locale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function stockholmToday(): string {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

interface Series {
  date: string;
  value: number;
}

interface Scale {
  x(date: string): number;
  y(value: number): number;
}

function makeScale(from: string, spanDays: number, min: number, max: number): Scale {
  const span = Math.max(1, spanDays);
  const range = max - min || 1;
  return {
    x: (date) => PAD_X + (daysBetween(from, date) / span) * (W - 2 * PAD_X),
    y: (value) => H - PAD_Y - ((value - min) / range) * (H - 2 * PAD_Y),
  };
}

/** Drop anything older than the window the card claims to show. */
function within(from: string, series: Series[]): Series[] {
  return series.filter((point) => daysBetween(from, point.date) >= 0);
}

/** Split a series into runs of points close enough in time to connect. */
function segments(series: Series[]): Series[][] {
  const out: Series[][] = [];
  let run: Series[] = [];
  for (const point of series) {
    const previous = run[run.length - 1];
    if (previous && daysBetween(previous.date, point.date) > MAX_GAP_DAYS) {
      out.push(run);
      run = [];
    }
    run.push(point);
  }
  if (run.length > 0) out.push(run);
  return out;
}

function polyline(points: Series[], scale: Scale, cls: string): SVGPolylineElement {
  return svgEl("polyline", {
    class: cls,
    points: points.map((p) => `${scale.x(p.date).toFixed(1)},${scale.y(p.value).toFixed(1)}`).join(" "),
  });
}

function dot(point: Series, scale: Scale, cls: string, r: number): SVGCircleElement {
  return svgEl("circle", {
    class: cls,
    cx: scale.x(point.date).toFixed(1),
    cy: scale.y(point.value).toFixed(1),
    r: String(r),
  });
}

const ZONE_KEYS: Record<string, Parameters<typeof t>[0]> = {
  deep: "resa.gki.zone.deep",
  high: "resa.gki.zone.high",
  moderate: "resa.gki.zone.moderate",
  low: "resa.gki.zone.low",
  none: "resa.gki.zone.none",
};

function zoneLabel(zone: string): string {
  const key = ZONE_KEYS[zone];
  return key ? t(key) : zone;
}

function zoneClass(zone: string): string {
  return ZONE_KEYS[zone] ? `gki-z-${zone}` : "gki-z-other";
}

const MOOD_KEYS: Record<number, Parameters<typeof t>[0]> = {
  1: "today.checkin.mood.1",
  2: "today.checkin.mood.2",
  3: "today.checkin.mood.3",
  4: "today.checkin.mood.4",
  5: "today.checkin.mood.5",
};

export function mountResa(root: HTMLElement): () => void {
  let disposed = false;
  // Runs once the cards are in the document, where heights can be measured.
  let afterMount: (() => void) | null = null;

  function shell(...nodes: Array<Node | string>): void {
    clear(root);
    root.append(el("h1", { text: t("resa.title") }), ...nodes);
  }

  // ── 1. weight ─────────────────────────────────────────────────────────────
  function weightCard(stats: Stats): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("resa.weight.title") }));

    // Fixed 60-day window so a short series sits where it belongs on the
    // timeline instead of being stretched across the whole card.
    const from = addDays(stockholmToday(), -(WINDOW_DAYS - 1));
    const raw = within(from, stats.weight.points.map((p) => ({ date: p.date, value: p.kg })));
    const smooth = within(from, stats.weight.smoothed.map((p) => ({ date: p.date, value: p.kg })));
    if (raw.length < 3) {
      card.append(el("p", { class: "muted", text: t("resa.weight.empty") }));
      return card;
    }

    if (stats.weight.direction_sv) {
      card.append(el("p", { class: "lead", text: stats.weight.direction_sv }));
    }

    const values = [...raw, ...smooth].map((p) => p.value);
    const min = Math.min(...values) - 0.4;
    const max = Math.max(...values) + 0.4;
    const scale = makeScale(from, WINDOW_DAYS - 1, min, max);

    const svg = chartRoot(`${t("resa.weight.title")} — ${t("resa.weight.period")}`);
    for (const point of raw) svg.append(dot(point, scale, "spark-raw", 2.2));
    for (const run of segments(smooth)) {
      if (run.length === 1) svg.append(dot(run[0]!, scale, "spark-head", 3));
      else svg.append(polyline(run, scale, "spark-line"));
    }
    const lastSmooth = smooth[smooth.length - 1];
    if (lastSmooth) svg.append(dot(lastSmooth, scale, "spark-head", 3.6));
    card.append(svg);

    const lastRaw = raw[raw.length - 1];
    if (lastRaw) {
      card.append(
        el("p", {
          class: "muted",
          text: `${t("resa.weight.latest")} ${fmtNumber(lastRaw.value, 1)} ${t(
            "resa.weight.unit"
          )} · ${fmtDate(lastRaw.date)}`,
        })
      );
    }
    card.append(el("p", { class: "muted small", text: t("resa.weight.period") }));
    return card;
  }

  // ── 2. GKI — the card exists only when there is something to show ─────────
  function gkiCard(stats: Stats): HTMLElement | null {
    const from = addDays(stockholmToday(), -(WINDOW_DAYS - 1));
    const points = stats.gki.filter((p) => daysBetween(from, p.date) >= 0);
    if (points.length === 0) return null;

    const card = el("div", { class: "card" });
    card.append(
      el("h2", { text: t("resa.gki.title") }),
      el("p", { class: "muted", text: t("resa.gki.hint") })
    );

    const series: Series[] = points.map((p) => ({ date: p.date, value: p.gki }));
    const values = series.map((p) => p.value);
    const min = Math.max(0, Math.min(...values) - 0.5);
    const max = Math.max(...values) + 0.5;
    const scale = makeScale(from, WINDOW_DAYS - 1, min, max);

    const svg = chartRoot(t("resa.gki.title"));
    // Zone bands are drawn only from thresholds the server sent; they are never
    // guessed client-side (the numbers live in the care profile).
    let bandFloor = min;
    for (const zone of stats.gki_zones ?? []) {
      const ceiling = zone.max === null ? max : Math.min(zone.max, max);
      if (ceiling <= bandFloor) continue;
      svg.append(
        svgEl("rect", {
          class: `gki-band ${zoneClass(zone.id)}`,
          x: String(PAD_X),
          y: scale.y(ceiling).toFixed(1),
          width: String(W - 2 * PAD_X),
          height: Math.max(0, scale.y(bandFloor) - scale.y(ceiling)).toFixed(1),
        })
      );
      bandFloor = ceiling;
      if (bandFloor >= max) break;
    }
    for (const run of segments(series)) {
      if (run.length > 1) svg.append(polyline(run, scale, "spark-line gki"));
    }
    points.forEach((point, index) => {
      const item = series[index];
      if (item) svg.append(dot(item, scale, `spark-gki ${zoneClass(point.zone)}`, 3.2));
    });
    card.append(svg);

    const latest = points[points.length - 1];
    if (latest) {
      card.append(
        el("p", {
          class: "lead",
          text: `${t("resa.gki.latest")} ${fmtNumber(latest.gki, 1)} · ${zoneLabel(latest.zone)}`,
        }),
        el("p", { class: "muted small", text: fmtDate(latest.date) })
      );
    }

    const seen = new Map<string, string>();
    for (const point of points) seen.set(point.zone, zoneLabel(point.zone));
    if (seen.size > 1) {
      const legend = el("div", { class: "legend" });
      for (const [zone, label] of seen) {
        legend.append(
          el("span", { class: "legend-item" }, [
            el("i", { class: `legend-dot ${zoneClass(zone)}`, "aria-hidden": "true" }),
            el("span", { text: label }),
          ])
        );
      }
      card.append(legend);
    }
    return card;
  }

  // ── 3. mood ───────────────────────────────────────────────────────────────
  function moodCard(stats: Stats): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("resa.mood.title") }));

    const from = addDays(stockholmToday(), -(MOOD_DAYS - 1));
    const points = stats.mood.filter((p) => daysBetween(from, p.date) >= 0);
    if (points.length === 0) {
      card.append(el("p", { class: "muted", text: t("resa.mood.empty") }));
      return card;
    }

    const scale = makeScale(from, MOOD_DAYS - 1, 0.6, 5.4);
    const svg = chartRoot(t("resa.mood.title"));
    for (const point of points) {
      svg.append(
        dot({ date: point.date, value: point.mood }, scale, `mood-dot mood-l${Math.round(point.mood)}`, 4)
      );
    }
    card.append(svg, el("p", { class: "muted", text: t("resa.mood.hint") }));

    const latest = points[points.length - 1];
    const word = latest ? MOOD_KEYS[Math.round(latest.mood)] : undefined;
    if (latest && word) {
      card.append(
        el("p", { class: "muted", text: `${t("resa.mood.latest")} ${t(word)} · ${fmtDate(latest.date)}` })
      );
    }
    return card;
  }

  // ── 4. the weekly letter ──────────────────────────────────────────────────
  function briefCard(stats: Stats, brief: { week_start?: string; html: string } | null): HTMLElement {
    const card = el("div", { class: "card" });
    card.append(el("h2", { text: t("resa.brief.title") }));
    if (!brief) {
      card.append(el("p", { class: "muted", text: t("brief.empty") }));
      return card;
    }
    const week = stats.latest_brief?.week_of ?? brief.week_start;
    if (week) {
      card.append(el("p", { class: "muted", text: `${t("resa.brief.weekOf")} ${fmtDate(week)}` }));
    }

    const paper = el("article", { class: "brief" });
    // Trusted content: generated by our own server, no third-party HTML.
    paper.innerHTML = brief.html;
    const clip = el("div", { class: "brief-clip clipped" }, [paper]);
    const toggle = el("button", { class: "btn", type: "button", text: t("resa.brief.more") });
    toggle.addEventListener("click", () => {
      const clipped = clip.classList.toggle("clipped");
      toggle.textContent = clipped ? t("resa.brief.more") : t("resa.brief.less");
    });
    card.append(clip, toggle);
    // A short letter needs no "read all of it" button.
    afterMount = () => {
      if (clip.scrollHeight <= clip.clientHeight + 4) {
        clip.classList.remove("clipped");
        toggle.remove();
      }
    };
    return card;
  }

  async function load(): Promise<void> {
    shell(el("p", { class: "muted", text: t("resa.loading") }));

    if (!getToken()) {
      shell(el("div", { class: "notice", text: t("resa.needLogin") }));
      return;
    }

    let stats: Stats;
    try {
      stats = await getStats();
    } catch {
      if (disposed) return;
      const retry = el("button", { class: "btn", type: "button", text: t("resa.retry") });
      retry.addEventListener("click", () => void load());
      shell(el("div", { class: "notice err", text: t("resa.error") }), retry);
      return;
    }
    if (disposed) return;

    const brief = await getLatestBrief().catch(() => null);
    if (disposed) return;

    const cards: HTMLElement[] = [weightCard(stats)];
    const gki = gkiCard(stats);
    if (gki) cards.push(gki);
    cards.push(moodCard(stats), briefCard(stats, brief));
    shell(...cards);
    afterMount?.();
    afterMount = null;
  }

  void load();
  return () => {
    disposed = true;
  };
}
